import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import {
  BsArrowRight,
  BsPlayFill,
  BsCodeSlash,
  BsRobot,
  BsGraphUpArrow,
  BsGlobe2,
  BsX
} from 'react-icons/bs';
import { useCMS } from '../../context/CMSContext';

const Header = () => {
  const navigate = useNavigate();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const { getSection } = useCMS();

  const homeCMS = getSection('home', {
    heroTag: 'TECHNOLOGY MEETS CREATIVITY',
    titleLine1: 'Digital Solutions',
    titleLine2: 'for a',
    highlightText1: 'Smarter',
    highlightText2: 'Tomorrow',
    description: 'Beta Communes helps businesses build, market and scale with powerful websites, AI solutions, and data-driven digital strategies.',
    primaryBtnText: 'Start Your Project',
    primaryBtnLink: '/contactus',
    secondaryBtnText: 'Watch Our Story',
    statsProjects: '250+',
    statsClients: '100+',
    statsExperience: '5+'
  });

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <>
      <section className="relative overflow-hidden bg-[#080c14] text-white pt-10 sm:pt-14 pb-0">

        {/* Background Workspace Image with Cinematic Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/hero_dev_workspace.jpg"
            alt="Developer Workspace"
            className="w-full h-full object-cover object-center lg:object-right opacity-45 lg:opacity-55"
            onError={(e) => {
              // Fallback gradient if file missing
              e.target.style.display = 'none';
            }}
          />
          {/* Gradients ensuring flawless contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#080c14] via-[#080c14]/85 to-[#080c14]/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#080c14] via-transparent to-[#080c14]/60"></div>
          {/* Subtle Orange Ambient Glow */}
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-[#f06600]/10 blur-[130px] rounded-full pointer-events-none"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center min-h-[520px] lg:min-h-[580px] py-8 lg:py-12">

            {/* Left Column: Hero Content */}
            <div className="lg:col-span-7 space-y-6 text-left" data-aos="fade-right">

              {/* Over-heading Tag */}
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#f06600]"></span>
                <span className="text-[11px] sm:text-xs font-bold tracking-[0.22em] text-slate-300 uppercase">
                  {homeCMS.heroTag || 'TECHNOLOGY MEETS CREATIVITY'}
                </span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-[60px] font-extrabold tracking-tight text-white leading-[1.12]">
                {homeCMS.titleLine1 || 'Digital Solutions'}<br />
                {homeCMS.titleLine2 || 'for a'}{' '}
                <span className="text-[#f06600]">{homeCMS.highlightText1 || 'Smarter'}</span><br />
                <span className="text-[#f06600]">{homeCMS.highlightText2 || 'Tomorrow'}</span>
              </h1>

              {/* Subtitle */}
              <p className="text-sm sm:text-base text-slate-300 max-w-lg leading-relaxed font-normal">
                {homeCMS.description || 'Beta Communes helps businesses build, market and scale with powerful websites, AI solutions, and data-driven digital strategies.'}
              </p>

              {/* CTA Action Buttons */}
              <div className="pt-2 flex flex-wrap items-center gap-3.5">
                <button
                  onClick={() => navigate(homeCMS.primaryBtnLink || '/contactus')}
                  className="bg-[#f06600] hover:bg-[#d05800] text-white font-bold px-6 sm:px-7 py-3.5 rounded-full shadow-lg shadow-orange-500/30 hover:shadow-orange-500/45 transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-xs sm:text-sm cursor-pointer"
                >
                  <span>{homeCMS.primaryBtnText || 'Start Your Project'}</span>
                  <BsArrowRight className="text-sm" />
                </button>

                <button
                  onClick={() => setShowVideoModal(true)}
                  className="bg-slate-900/80 hover:bg-slate-800 text-slate-200 border border-slate-700/80 hover:border-slate-600 font-medium px-5 sm:px-6 py-3.5 rounded-full shadow-md transition-all duration-200 flex items-center justify-center gap-2.5 text-xs sm:text-sm cursor-pointer backdrop-blur-sm"
                >
                  <span className="w-5 h-5 rounded-full border border-slate-400/60 flex items-center justify-center text-[10px] text-white">
                    ▶
                  </span>
                  <span>{homeCMS.secondaryBtnText || 'Watch Our Story'}</span>
                </button>
              </div>

            </div>

            {/* Right Column: Visual Pill Tags & Handwritten Note */}
            <div className="lg:col-span-5 relative flex flex-col justify-center items-end" data-aos="fade-left">

              {/* Handwritten Note Top Right with Arrow */}
              <div className="hidden sm:flex flex-col items-end mb-6 mr-4 text-right select-none">
                <div className="font-caveat text-xl sm:text-2xl text-amber-200/90 leading-tight transform -rotate-6">
                  Ideas<br />
                  Technology<br />
                  Growth<br />
                  Together
                </div>
                {/* Hand-drawn arrow */}
                <svg className="w-10 h-10 text-amber-200/80 transform rotate-12 -mt-1 mr-2" viewBox="0 0 50 50" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5 C 28 15, 38 30, 26 42" />
                  <path d="M22 36 L 26 42 L 34 38" />
                </svg>
              </div>

              {/* Vertical Stack of Sleek Floating Chips matching Mockup */}
              <div className="space-y-2.5 w-full sm:w-auto min-w-[210px]">

                <div className="bg-[#0e1422]/85 hover:bg-[#131b2e] border border-slate-700/70 hover:border-[#f06600]/60 backdrop-blur-md px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-3 transition-all duration-300 transform hover:-translate-x-1 cursor-default group">
                  <div className="w-7 h-7 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-[#f06600] text-sm group-hover:scale-110 transition-transform">
                    <BsCodeSlash />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-slate-200 group-hover:text-white">
                    Web Development
                  </span>
                </div>

                <div className="bg-[#0e1422]/85 hover:bg-[#131b2e] border border-slate-700/70 hover:border-[#f06600]/60 backdrop-blur-md px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-3 transition-all duration-300 transform hover:-translate-x-1 cursor-default group">
                  <div className="w-7 h-7 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-[#f06600] text-sm group-hover:scale-110 transition-transform">
                    <BsRobot />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-slate-200 group-hover:text-white">
                    AI Solutions
                  </span>
                </div>

                <div className="bg-[#0e1422]/85 hover:bg-[#131b2e] border border-slate-700/70 hover:border-[#f06600]/60 backdrop-blur-md px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-3 transition-all duration-300 transform hover:-translate-x-1 cursor-default group">
                  <div className="w-7 h-7 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-[#f06600] text-sm group-hover:scale-110 transition-transform">
                    <BsGraphUpArrow />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-slate-200 group-hover:text-white">
                    Digital Marketing
                  </span>
                </div>

                <div className="bg-[#0e1422]/85 hover:bg-[#131b2e] border border-slate-700/70 hover:border-[#f06600]/60 backdrop-blur-md px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-3 transition-all duration-300 transform hover:-translate-x-1 cursor-default group">
                  <div className="w-7 h-7 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-[#f06600] text-sm group-hover:scale-110 transition-transform">
                    <BsGlobe2 />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-slate-200 group-hover:text-white">
                    Web Applications
                  </span>
                </div>

              </div>

            </div>

          </div>

        </div>

        {/* Hero Bottom Stats Strip matching Mockup */}
        <div className="border-t border-slate-800/80 bg-[#060910]/95 backdrop-blur-md relative z-20 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">

            {/* Left: Scroll to Explore */}
            <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
              <div className="w-4 h-6 rounded-full border border-slate-500 flex items-start justify-center p-1">
                <span className="w-1 h-1 rounded-full bg-[#f06600] animate-bounce"></span>
              </div>
              <span>Scroll to Explore</span>
            </div>

            {/* Middle: 3 Core Metrics */}
            <div className="flex items-center gap-8 sm:gap-14 text-center">
              <div>
                <div className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
                  {homeCMS.statsProjects || "250+"}
                </div>
                <div className="text-[11px] text-slate-400 font-medium mt-0.5">Projects Delivered</div>
              </div>
              <div className="w-px h-6 bg-slate-800 hidden sm:block"></div>
              <div>
                <div className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
                  {homeCMS.statsClients || "100+"}
                </div>
                <div className="text-[11px] text-slate-400 font-medium mt-0.5">Happy Clients</div>
              </div>
              <div className="w-px h-6 bg-slate-800 hidden sm:block"></div>
              <div>
                <div className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
                  {homeCMS.statsExperience || "5+"}
                </div>
                <div className="text-[11px] text-slate-400 font-medium mt-0.5">Years of Experience</div>
              </div>
            </div>

            {/* Right: PLAY VIDEO button */}
            <button
              onClick={() => setShowVideoModal(true)}
              className="flex items-center gap-2.5 text-left group cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-[#f06600] border border-white/20 group-hover:border-[#f06600] flex items-center justify-center text-white transition-all">
                <BsPlayFill className="text-base ml-0.5" />
              </div>
              <div>
                <div className="text-[11px] font-bold text-white uppercase tracking-wider group-hover:text-[#f06600] transition-colors">
                  PLAY VIDEO
                </div>
                <div className="text-[10px] text-slate-400">
                  See how we work
                </div>
              </div>
            </button>

          </div>
        </div>

      </section>

      {/* Video Modal Popup */}
      {showVideoModal && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="relative w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <span className="text-xs font-bold text-slate-300 tracking-wider uppercase">Beta Communes • Inside Our Process</span>
              <button
                onClick={() => setShowVideoModal(false)}
                className="w-7 h-7 rounded-full bg-slate-800 text-slate-300 hover:text-white flex items-center justify-center text-lg"
              >
                <BsX />
              </button>
            </div>
            <div className="relative pt-[56.25%] bg-black">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ?autoplay=1"
                title="Beta Communes Showcase"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
